import { StatusStyle, GenericStatusInfoIconName, GenericStatusIndicator } from '@caresend/types';
import { defineComponent, computed } from 'vue';
import __vue_component__$1 from '../../buttons/ButtonComponent/ButtonComponent.vue.js';
import { ButtonStyle } from '../../buttons/ButtonComponent/model.js';
import __vue_component__ from '../../cards/BasicCard/BasicCard.vue.js';
import { CardStyle } from '../../cards/BasicCard/model.js';
import { StatusIndicatorSize } from '../StatusIndicator/model.js';
import __vue_component__$3 from '../StatusIndicator/StatusIndicator.vue.js';
import { StatusIndicatorWithLabelStyle } from './model.js';
import __vue_component__$4 from './StatusInfoSubtitle.vue.js';
import __vue_component__$2 from '../../ui/Skeleton.vue.js';

/** Wraps `StatusIndicator` into a parent component to display title, subtitle and callToAction. */
var script = defineComponent({
    name: 'StatusIndicatorWithLabel',
    components: {
        BasicCard: __vue_component__,
        ButtonComponent: __vue_component__$1,
        Skeleton: __vue_component__$2,
        StatusIndicator: __vue_component__$3,
        StatusInfoSubtitle: __vue_component__$4,
    },
    props: {
        clickable: Boolean,
        componentStyle: {
            type: String,
            default: 'default',
        },
        darkMode: Boolean,
        fullWidth: Boolean,
        isBackgroundColored: Boolean,
        isBordered: Boolean,
        isLoading: Boolean,
        noCard: Boolean,
        noPaddingLeft: Boolean,
        statusInfo: {
            type: Object,
            required: true,
        },
        statusSize: {
            type: String,
            default: StatusIndicatorSize.MD,
        },
        statusStyle: {
            type: String,
            default: StatusStyle.SQUARE,
        },
        /**
         * If true, append the subtitle(s) where the title is rendered, rather
         * than placing them below the title.
         */
        subtitleInTitle: Boolean,
        /**
         * If true, place the subtitle(s) to the right of the title, rather than
         * below the title. Callback button will be placed to the right of the
         * subtitle(s). Currently supports only one callback button.
         */
        subtitleOnRight: Boolean,
        to: {
            type: [String, Object],
            default: null,
        },
    },
    setup(props, { emit }) {
        const isArray = (value) => Array.isArray(value);
        const emitClick = (event) => {
            if (props.clickable)
                return emit('click', event);
        };
        const computedTitle = computed(() => {
            const { title } = props.statusInfo;
            if (props.subtitleInTitle) {
                const { subtitle } = props.statusInfo;
                if (!subtitle)
                    return title;
                const subtitleArray = isArray(subtitle) ? subtitle : [subtitle];
                return `${title} ${subtitleArray.join(', ')}`;
            }
            return title;
        });
        const isOrdersProcedureCardStyle = computed(() => props.componentStyle === StatusIndicatorWithLabelStyle.ORDERS_PROCEDURE_CARD);
        const cardStyle = computed(() => {
            if (props.noCard)
                return;
            if (isOrdersProcedureCardStyle.value)
                return CardStyle.WHITE;
            return CardStyle.TRANSPARENT;
        });
        const classNames = computed(() => {
            const names = [];
            if (props.noCard) {
                names.push('StatusIndicatorWithLabel--no-card');
            }
            if (props.statusSize === StatusIndicatorSize.XS) {
                names.push('StatusIndicatorWithLabel--xs');
            }
            else if (props.statusSize === StatusIndicatorSize.SM) {
                names.push('StatusIndicatorWithLabel--sm');
            }
            if (props.darkMode)
                names.push('StatusIndicatorWithLabel--dark-mode');
            if (props.componentStyle === StatusIndicatorWithLabelStyle.ORDERS_PROCEDURE_CARD) {
                names.push('StatusIndicatorWithLabel--order-procedure-card');
            }
            return names;
        });
        const labelsClassNames = computed(() => {
            const names = [];
            if (props.noPaddingLeft) {
                names.push('StatusIndicatorWithLabel__labels-no-padding-left');
            }
            return names;
        });
        const computedStatusSize = computed(() => {
            if (isOrdersProcedureCardStyle.value) {
                if (props.statusInfo.iconName === GenericStatusInfoIconName.DELIVERY) {
                    return StatusIndicatorSize.MD;
                }
                return StatusIndicatorSize.SM;
            }
            return props.statusSize;
        });
        const indicatorAndLabelClassNames = computed(() => {
            const classes = [];
            if (props.isBordered) {
                classes.push('StatusIndicatorWithLabel__indicator-and-label--with-border');
            }
            if (props.fullWidth) {
                classes.push('StatusIndicatorWithLabel__indicator-and-label--full-width');
            }
            if (props.isBackgroundColored) {
                classes.push('StatusIndicatorWithLabel__indicator-and-label--shadow');
                switch (props.statusInfo.color) {
                    case GenericStatusIndicator.BLUE:
                        classes.push('background-blue-25');
                        break;
                    case GenericStatusIndicator.GREEN:
                        classes.push('background-green-25');
                        break;
                    case GenericStatusIndicator.PURPLE:
                        classes.push('background-purple-50');
                        break;
                    case GenericStatusIndicator.RED:
                        classes.push('background-red-25');
                        break;
                    case GenericStatusIndicator.ORANGE:
                        classes.push('background-orange-50');
                }
            }
            return classes;
        });
        const computedStatusStyle = computed(() => {
            if (isOrdersProcedureCardStyle.value)
                return StatusStyle.CIRCLE;
            return props.statusStyle;
        });
        const computedSubtitleOnRight = computed(() => {
            if (isOrdersProcedureCardStyle.value)
                return true;
            return props.subtitleOnRight;
        });
        const callBackButtonLabelStyle = computed(() => props.darkMode ? ButtonStyle.LINK_LIGHT_GREY : ButtonStyle.LINK);
        const componentIs = computed(() => props.noCard ? 'div' : __vue_component__);
        const clickCallBackButton = (event) => {
            var _a, _b;
            event.preventDefault();
            (_b = (_a = props.statusInfo).callBackButton) === null || _b === void 0 ? void 0 : _b.call(_a);
            // @deprecated - Once usages of @clickCallBackButton are removed,
            // remove the emit.
            emit('clickCallBackButton');
        };
        const clickCallBackButtonSecondary = (event) => {
            var _a, _b;
            event.preventDefault();
            (_b = (_a = props.statusInfo).callBackButtonSecondary) === null || _b === void 0 ? void 0 : _b.call(_a);
        };
        return {
            callBackButtonLabelStyle,
            cardStyle,
            classNames,
            clickCallBackButton,
            clickCallBackButtonSecondary,
            componentIs,
            computedStatusSize,
            computedStatusStyle,
            computedSubtitleOnRight,
            computedTitle,
            emitClick,
            indicatorAndLabelClassNames,
            isArray,
            labelsClassNames,
        };
    },
});

export { script as default };
